import React, { useState, useCallback, useEffect } from "react";
import Cookies from "js-cookie";
import {
  NotesAndPicturesContext,
  type OverlayKind,
  type PersistedOverlayItem,
} from "../context/NotesAndPicturesContext";
import { NOTES_COOKIE_KEY } from "../utils/constants";

const DATA_URL_PREFIX = "crime_stalker_note_img_";

function dataUrlKey(id: string) {
  return `${DATA_URL_PREFIX}${id}`;
}

function saveDataUrl(id: string, url: string | null) {
  try {
    if (url && url.startsWith("data:")) {
      localStorage.setItem(dataUrlKey(id), url);
    } else {
      localStorage.removeItem(dataUrlKey(id));
    }
  } catch {
    // ignore quota errors
  }
}

function loadDataUrl(id: string): string | null {
  try {
    return localStorage.getItem(dataUrlKey(id));
  } catch {
    return null;
  }
}

function removeDataUrl(id: string) {
  try {
    localStorage.removeItem(dataUrlKey(id));
  } catch {
    // ignore
  }
}

function clearAllDataUrls() {
  try {
    Object.keys(localStorage)
      .filter((k) => k.startsWith(DATA_URL_PREFIX))
      .forEach((k) => localStorage.removeItem(k));
  } catch {
    // ignore
  }
}

const DEFAULT_POSITION = { x: 200, y: 200 };

function getDefaultItems(): PersistedOverlayItem[] {
  const W = window.innerWidth;
  const H = window.innerHeight;
  // Picture: top-centre, M card is 280px wide
  const picW = 280;
  const picX = Math.max(0, Math.round(W / 2 - picW / 2)) + 100;
  const picY = 15;
  // Note: right-middle, S card is 200px wide, keep 16px from right edge
  const noteW = 200;
  const noteX = Math.max(0, W - noteW - 16);
  const noteY = Math.max(0, Math.round(H / 2 - 80));
  return [
    {
      id: "default-note",
      kind: "note",
      size: "S",
      position: { x: noteX, y: noteY },
      noteText: "Who is D.B. Cooper?",
      pictureUrl: null,
      collapsed: false,
    },
    {
      id: "default-picture",
      kind: "picture",
      size: "M",
      position: { x: picX, y: picY },
      noteText: "",
      pictureUrl:
        "https://www.fbi.gov/image-repository/cooper-sketches.jpeg/@@images/image/high",
      collapsed: false,
    },
  ];
}

function loadFromCookie(): PersistedOverlayItem[] {
  try {
    const str = Cookies.get(NOTES_COOKIE_KEY);
    if (!str) return getDefaultItems();
    const raw = JSON.parse(str);
    if (!Array.isArray(raw)) return getDefaultItems();
    // Cookie exists – honour it even if empty (user closed all overlays)
    return (raw as any[])
      .filter(
        (item) =>
          item &&
          typeof item.id === "string" &&
          (item.kind === "note" || item.kind === "picture"),
      )
      .map((item) => ({
        id: item.id,
        kind: item.kind as OverlayKind,
        size: ["S", "M", "L"].includes(item.size)
          ? item.size
          : item.kind === "note"
            ? "S"
            : "M",
        position:
          item.position &&
          typeof item.position.x === "number" &&
          typeof item.position.y === "number"
            ? item.position
            : DEFAULT_POSITION,
        noteText: typeof item.noteText === "string" ? item.noteText : "",
        // Restore data URL from localStorage if cookie had none
        pictureUrl:
          typeof item.pictureUrl === "string"
            ? item.pictureUrl
            : loadDataUrl(item.id),
        collapsed: item.collapsed === true,
      }));
  } catch {
    return getDefaultItems();
  }
}

function saveToCookie(items: PersistedOverlayItem[]) {
  try {
    // Sync data URLs into localStorage; strip them from the cookie
    items.forEach((item) => saveDataUrl(item.id, item.pictureUrl));
    const sanitized = items.map((item) => ({
      ...item,
      pictureUrl:
        item.pictureUrl && !item.pictureUrl.startsWith("data:")
          ? item.pictureUrl
          : null,
    }));
    Cookies.set(NOTES_COOKIE_KEY, JSON.stringify(sanitized), {
      expires: 365,
      sameSite: "Lax",
      path: "/",
    });
  } catch {
    // ignore
  }
}

export function NotesAndPicturesProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [items, setItems] = useState<PersistedOverlayItem[]>(() =>
    loadFromCookie(),
  );

  useEffect(() => {
    saveToCookie(items);
  }, [items]);

  const addItem = useCallback((kind: OverlayKind) => {
    setItems((prev) => [
      ...prev,
      {
        id: `${kind}-${Date.now()}-${Math.random()}`,
        kind,
        size: kind === "note" ? "S" : "M",
        position: {
          x: Math.max(0, window.innerWidth / 2 - 140),
          y: Math.max(0, window.innerHeight / 2 - 100),
        },
        noteText: "",
        pictureUrl: null,
        collapsed: false,
      },
    ]);
  }, []);

  const removeItem = useCallback((id: string) => {
    removeDataUrl(id);
    setItems((prev) => prev.filter((i) => i.id !== id));
  }, []);

  const updateItem = useCallback(
    (id: string, patch: Partial<Omit<PersistedOverlayItem, "id" | "kind">>) => {
      setItems((prev) =>
        prev.map((i) => (i.id === id ? { ...i, ...patch } : i)),
      );
    },
    [],
  );

  const importItems = useCallback((incoming: PersistedOverlayItem[]) => {
    setItems(incoming);
  }, []);

  const clearItems = useCallback(() => {
    clearAllDataUrls();
    setItems(getDefaultItems());
  }, []);

  return (
    <NotesAndPicturesContext.Provider
      value={{
        items,
        addItem,
        removeItem,
        updateItem,
        importItems,
        clearItems,
      }}
    >
      {children}
    </NotesAndPicturesContext.Provider>
  );
}
